'use client';

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  accountType?: 'smb' | 'agency' | 'creator' | 'viewer';
  /** Brand viewer only: whose placements this login may read. */
  viewer?: { agencyId: string; agencyName: string; brandId: string; brandName: string; brandLogo: string | null };
  /** Тип ещё не выбран (OAuth с вкладки «Вход») — кабинет ведёт на /auth/account-type. */
  accountTypePending?: boolean;
  agencyApproved?: boolean;
  avatar?: string;
  tokens: number;
  plan: 'free' | 'basic' | 'pro' | 'enterprise';
  authProvider?: 'email' | 'google' | 'apple';
  partnerAgency?: string | null;
  subscription?: {
    status?: string;
    billingCycle?: 'monthly' | 'yearly';
    currentPeriodEnd?: string;
    cancelAtPeriodEnd?: boolean;
  };
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  checkAuth: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      // Check localStorage token first
      const localToken = localStorage.getItem('token');

      if (localToken) {
        // Validate token with API
        const response = await fetch('/api/auth/me', {
          headers: {
            'Authorization': `Bearer ${localToken}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          return;
        } else {
          // Token invalid, remove it
          localStorage.removeItem('token');
        }
      }

      // The OAuth token cookie is readable by the client. Avoid an expected
      // 401 request for every anonymous visitor and only validate when a
      // credential is actually present.
      const hasAuthCookie = document.cookie
        .split(';')
        .some((cookie) => cookie.trim().startsWith('token='));

      if (!hasAuthCookie) {
        setUser(null);
        return;
      }

      const response = await fetch('/api/auth/me');
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      setUser(null);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        loading,
        checkAuth,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // Return default values during SSR or before provider is mounted
    return {
      user: null,
      isAuthenticated: false,
      loading: true,
      checkAuth: async () => {},
      logout: async () => {},
    };
  }
  return context;
}
